import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { Redirect } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { injectIntl } from 'react-intl';
import qs from 'query-string';

import SeoMetaTags from 'volto-ministerio-publico-federal/components/SEO/SeoMetaTags';
import SeoStructuredData from 'volto-ministerio-publico-federal/components/SEO/SeoStructuredData';
import Comments from '@plone/volto/components/theme/Comments/Comments';
import Toolbar from '@plone/volto/components/manage/Toolbar/Toolbar';
import { listActions } from '@plone/volto/actions/actions/actions';
import { getContent } from '@plone/volto/actions/content/content';
import { getSlots } from 'mpf-slots-frontend/actions';
import BodyClass from '@plone/volto/helpers/BodyClass/BodyClass';
import {
  getBaseUrl,
  flattenToAppURL,
  stripQuerystring,
} from '@plone/volto/helpers/Url/Url';
import { getLayoutFieldname } from '@plone/volto/helpers/Content/Content';
import { hasApiExpander } from '@plone/volto/helpers/Utils/Utils';
import { AlternateHrefLangs } from '@plone/volto/components/theme/AlternateHrefLangs/AlternateHrefLangs';

import config from '@plone/volto/registry';
import SlotRenderer from 'mpf-slots-frontend/components/theme/SlotRenderer/SlotRenderer';
import ContentContainer from 'mpf-slots-frontend/components/theme/ContentContainer/ContentContainer';
import PrintMasthead from 'volto-ministerio-publico-federal/components/Print/PrintMasthead';
import PrintFooter from 'volto-ministerio-publico-federal/components/Print/PrintFooter';
import { toAppPath } from 'volto-ministerio-publico-federal/helpers/Url/Url';

const stripTrailingSlash = (path) =>
  path.length > 1 ? path.replace(/\/+$/, '') : path;

/** Se os slots no Redux são os desta página (o `@id` é `<página>/@slots`). */
export const slotsLoadedFor = (slotsId, pathname) =>
  !!slotsId &&
  flattenToAppURL(slotsId).replace(/\/@slots$/, '') ===
    getBaseUrl(pathname).replace(/\/+$/, '');

export class View extends Component {
  static propTypes = {
    actions: PropTypes.shape({
      object: PropTypes.arrayOf(PropTypes.object),
      object_buttons: PropTypes.arrayOf(PropTypes.object),
      user: PropTypes.arrayOf(PropTypes.object),
    }),
    listActions: PropTypes.func.isRequired,
    getContent: PropTypes.func.isRequired,
    getSlots: PropTypes.func.isRequired,
    pathname: PropTypes.string.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
      pathname: PropTypes.string,
    }).isRequired,
    versionId: PropTypes.string,
    content: PropTypes.shape({
      layout: PropTypes.string,
      allow_discussion: PropTypes.bool,
      title: PropTypes.string,
      description: PropTypes.string,
      '@type': PropTypes.string,
      subjects: PropTypes.arrayOf(PropTypes.string),
      is_folderish: PropTypes.bool,
    }),
    error: PropTypes.shape({
      status: PropTypes.number,
    }),
  };

  static defaultProps = {
    actions: null,
    content: null,
    versionId: null,
    error: null,
    getSlots: () => null,
  };

  state = {
    hasObjectButtons: null,
    isClient: false,
  };

  componentDidMount() {
    if (!hasApiExpander('actions', getBaseUrl(this.props.pathname))) {
      this.props.listActions(getBaseUrl(this.props.pathname));
    }

    this.props.getContent(
      getBaseUrl(this.props.pathname),
      this.props.versionId,
    );
    // O SSR (`App`) já carrega @slots da página: na hidratação não repete.
    if (!slotsLoadedFor(this.props.slotsId, this.props.pathname)) {
      this.props.getSlots(getBaseUrl(this.props.pathname));
    }
    this.setState({ isClient: true });
    this.rememberRendered();
  }

  componentDidUpdate() {
    this.rememberRendered();
  }

  // O que estava na tela por último, para o carregamento da próxima página
  // manter a lateral e a altura (sem a view antiga, ver `render`).
  lastRendered = null;

  rememberRendered = () => {
    if (!this.isContentForCurrentRoute()) {
      return;
    }
    this.lastRendered = {
      content:
        this.props.content['@components']?.['default-page-content']?.data ||
        this.props.content,
      height: this.lastRendered?.height || 0,
    };
  };

  // Medida no clique, com a página antiga já completa (blocos assíncronos
  // terminam depois do último update do View).
  rememberHeight = () => {
    const view = document.getElementById('view');
    const column =
      view?.querySelector('.mpf-internal__contents, .__contents') || view;
    if (this.lastRendered && column) {
      this.lastRendered.height = column.offsetHeight;
    }
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.pathname !== this.props.pathname) {
      this.rememberHeight();
      if (!hasApiExpander('actions', getBaseUrl(nextProps.pathname))) {
        this.props.listActions(getBaseUrl(nextProps.pathname));
      }

      this.props.getContent(
        getBaseUrl(nextProps.pathname),
        this.props.versionId,
      );
      this.props.getSlots(getBaseUrl(nextProps.pathname));
    }

    if (nextProps.actions.object_buttons) {
      const objectButtons = nextProps.actions.object_buttons;
      this.setState({
        hasObjectButtons: !!objectButtons.length,
      });
    }
  }

  getViewDefault = () => config.views.defaultView;

  getViewByType = (contentType) =>
    config.views.contentTypesViews[contentType] || null;

  getViewByLayout = (layout) => config.views.layoutViews[layout] || null;

  resolveView = (content) =>
    this.getViewByLayout(content[getLayoutFieldname(content)]) ||
    this.getViewByType(content['@type']) ||
    this.getViewDefault();

  cleanViewName = (dirtyDisplayName) =>
    dirtyDisplayName
      .replace('Connect(', '')
      .replace('injectIntl(', '')
      .replace(')', '')
      .replace('connect(', '')
      .toLowerCase();

  // `content` pode ser um objeto truthy SEM `@id`: ao sair do Edit o
  // `resetContent` zera `state.content.data` e, logo depois, o
  // UNLOCK_CONTENT_SUCCESS o reescreve como `{ ...null, lock: {...} }`.
  // Sem a guarda no `@id`, o `flattenToAppURL` devolvia undefined e o
  // `stripQuerystring` quebrava a tela ao salvar.
  //
  // `content['@id']` nunca vem com `/` final (a API do Plone já o
  // normaliza), mas `getBaseUrl(pathname)` só remove sufixos conhecidos
  // (`/edit`, `/view`...) e nunca uma `/` final solta — acessar uma URL
  // com `/` no final fazia essa comparação nunca bater, prendendo a tela
  // no `renderLoadingState()` (conteúdo em branco) para sempre, mesmo com
  // o conteúdo já carregado. `stripTrailingSlash` normaliza os dois lados
  // antes de comparar.
  isContentForCurrentRoute = () =>
    !!this.props.content?.['@id'] &&
    stripTrailingSlash(
      stripQuerystring(flattenToAppURL(this.props.content['@id'])),
    ) === stripTrailingSlash(getBaseUrl(this.props.pathname));

  renderLoadingState = () => (
    <div id="view" tabIndex="-1">
      <div aria-busy="true" style={{ padding: '2rem', textAlign: 'center' }} />
    </div>
  );

  render() {
    const { views } = config;
    // Conteúdo renomeado: o Plone responde 301/302 e o `Api.js` rejeita com
    // `{ code, url }`. A URL vem com a origem que o Zope enxerga (host do VHM,
    // porta real do processo, id do site), que não é a origem do app — daí o
    // `toAppPath`, que fica só com o caminho interno. Sem destino utilizável
    // (URL vazia ou apontando para a própria rota, o que faria loop de
    // `<Redirect>`) o fluxo cai no tratamento de erro abaixo.
    const isRedirect = [301, 302].includes(this.props.error?.code);
    const redirectTo = isRedirect ? toAppPath(this.props.error.url) : null;
    const isSameRoute =
      !!redirectTo &&
      redirectTo ===
        stripTrailingSlash(getBaseUrl(this.props.location.pathname));

    if (redirectTo && !isSameRoute) {
      return <Redirect to={`${redirectTo}${this.props.location.search}`} />;
    }

    if (this.props.error && !this.props.connectionRefused) {
      let FoundView;
      if (isRedirect) {
        // Redirect que não deu para resolver: a URL antiga não existe mais.
        FoundView = views.errorViews['404'];
      } else if (this.props.error.status === undefined) {
        FoundView = views.errorViews.corsError;
      } else {
        FoundView = views.errorViews[this.props.error.status.toString()];
      }
      if (!FoundView) {
        FoundView = views.errorViews['404'];
      }
      return (
        <div id="view" tabIndex="-1">
          <BodyClass
            className={
              FoundView.displayName
                ? `view-${this.cleanViewName(FoundView.displayName)}`
                : null
            }
          />
          <FoundView {...this.props} />
        </div>
      );
    }
    if (!this.props.content) {
      return <span />;
    }

    // Enquanto o conteúdo da rota nova não chega, a view antiga não renderiza
    // (com a rota nova ela mostrava dados de outra pasta, ver 310e3387), mas a
    // lateral e a altura da página anterior ficam. A árvore é a mesma dos dois
    // jeitos: com o `ContentContainer` em outra posição o React remontaria a
    // lateral e o menu sumiria e voltaria a cada clique.
    const loading = !this.isContentForCurrentRoute();
    if (loading && !this.lastRendered) {
      return this.renderLoadingState();
    }

    // O item padrão da pasta vem serializado no próprio GET, pelo expander
    // `default-page-content` (ver `config/api-expanders.ts`). Antes isso era
    // um fetch no `componentDidUpdate`, que não roda no servidor: no SSR o
    // render retornava o estado de carregamento aqui, ANTES das meta tags, e
    // toda pasta com item padrão — a home inclusive — saía com <title> vazio,
    // sem description/canonical/Open Graph e com o corpo vazio.
    const defaultPageContent =
      this.props.content['@components']?.['default-page-content']?.data;

    const renderedContent = loading
      ? this.lastRendered.content
      : defaultPageContent || this.props.content;

    const RenderedView = loading ? null : this.resolveView(renderedContent);

    return (
      <div id="view" tabIndex="-1">
        {/* As meta tags descrevem o que o visitante VÊ (`renderedContent`,
            que numa pasta com item padrão é o item). Já a URL canônica é a
            da PASTA — é o endereço que ele acessou e que vai compartilhar.
            Antes ambos saíam de `this.props.content`, e numa pasta com item
            padrão o título, a descrição e o Open Graph descreviam a pasta, e
            não a página exibida. */}
        {!loading && (
          <SeoMetaTags
            content={renderedContent}
            canonicalPath={flattenToAppURL(this.props.content['@id'])}
          />
        )}
        {!loading && (
          <SeoStructuredData
            content={renderedContent}
            canonicalPath={flattenToAppURL(this.props.content['@id'])}
          />
        )}
        {!loading && <AlternateHrefLangs content={renderedContent} />}
        {!loading && (
          <BodyClass
            className={
              RenderedView.displayName
                ? `view-${this.cleanViewName(RenderedView.displayName)}`
                : null
            }
          />
        )}
        {/* Cabecalho da folha impressa: marca do MPF e as datas. O cabecalho
            do site inteiro fica fora do papel (`theme/styles/_print.scss`).
            A data acompanha o que o visitante VE (`renderedContent`, que
            numa pasta com item padrao e o item). */}
        {!loading && <PrintMasthead effective={renderedContent.effective} />}
        <SlotRenderer name="aboveContent" content={renderedContent} />
        <ContentContainer content={renderedContent}>
          {loading ? (
            <div
              aria-busy="true"
              style={{
                padding: '2rem',
                textAlign: 'center',
                minHeight: this.lastRendered.height || undefined,
              }}
            />
          ) : (
            <RenderedView
              key={flattenToAppURL(renderedContent['@id'])}
              content={renderedContent}
              location={this.props.location}
              token={this.props.token}
              history={this.props.history}
            />
          )}
        </ContentContainer>
        <SlotRenderer name="belowContent" content={renderedContent} />
        {/* Rodape so-impressao: fecha a folha com o endereco da pagina — QR
            code na noticia, URL em texto nos demais tipos. A URL sai de
            `this.props.content` (o endereco que o visitante acessou, igual ao
            canonical acima) e nao de `renderedContent`, que numa pasta com
            item padrao seria o item; ja o `@type` acompanha o que ele VE, que
            e quem define o formato do rodape. */}
        {!loading && (
          <PrintFooter
            contentId={this.props.content['@id']}
            contentType={renderedContent['@type']}
          />
        )}
        {!loading && this.props.content.allow_discussion && (
          <Comments pathname={this.props.pathname} />
        )}
        {this.state.isClient &&
          createPortal(
            <Toolbar pathname={this.props.pathname} inner={<span />} />,
            document.getElementById('toolbar'),
          )}
      </div>
    );
  }
}

export default compose(
  injectIntl,
  connect(
    (state, props) => ({
      actions: state.actions.actions,
      token: state.userSession.token,
      content: state.content.data,
      error: state.content.get.error,
      apiError: state.apierror.error,
      connectionRefused: state.apierror.connectionRefused,
      pathname: props.location.pathname,
      slotsId: state.slots?.data?.['@id'],
      versionId:
        qs.parse(props.location.search) &&
        qs.parse(props.location.search).version,
    }),
    {
      listActions,
      getContent,
      getSlots,
    },
  ),
)(View);
